// @givmo/compliance — the documents the organization keeps for its filings (CCM-WEB2, surface 6;
// re-composed at CCM-R1-INTAKE (a): frames 06 and 12).
//
// ══ WHAT THIS SURFACE IS ═══════════════════════════════════════════════════════════════════════
//
// The documents the organization has given us: the confirmation letters jurisdictions issued it, the
// filings it sent, and its own papers. It is a filing cabinet, not an assessment — nothing here
// scores a document, judges whether the right ones are present, or says anything about the
// organization's standing anywhere. Each row says who added it, when, and what the organization's
// record points at it for ("Used for"), and nothing about what it says.
//
// ⚠ **A DOCUMENT IS NEVER CHECKED, AND NO COPY HERE MAY IMPLY IT WAS.** The platform records that a
// file was uploaded, by whom and when, and reads its size back from storage. It does not open it,
// does not confirm it says what the organization believes it says, and does not compute its digest.
//
// ══ CITED MEANS FROZEN, AND THE CONTROL IS ABSENT RATHER THAN DISABLED ═════════════════════════
//
// Once anything in the record names a document it can never be removed — and the freeze does NOT
// lift when the reference is later dropped, because the append-only event log goes on recording that
// this document backed that filing. So a cited document's menu simply has no "Remove". Not a greyed
// one, not one that explains itself on click: a capability which is not there shows nothing. The
// wire answers "is it cited" directly (`is_attached`), so the affordance is read rather than inferred.
//
// ══ DOWNLOADED, NEVER PREVIEWED ════════════════════════════════════════════════════════════════
//
// "Open" downloads the document as stored (an attachment, CCM-WEB2): a Word file included, nothing
// here renders one in place.

import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Menu,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { Building2, FolderOpen, MoreHorizontal, Upload } from 'lucide-react';
import { useArtifactDownload } from './artifact-download';
import { VAULT_UNAVAILABLE_COPY } from './artifact-upload';
import { complianceAiSettingQueryKey, decodeComplianceAiSetting } from './compliance-ai-setting';
import { useCompliance } from './compliance-context';
import {
  displayArtifactSize,
  displayLocalDate,
  extractArtifacts,
  extractObligationArtifacts,
  isArtifactCited,
  isArtifactDeletable,
  isModuleUnavailable,
  isVaultStorageUnavailable,
  serverMessage,
  type ComplianceArtifact,
  type ObligationArtifact,
} from './compliance-decode';
import { documentUses, type DocumentUse } from './compliance-documents-model';
import { useFilingsComposition } from './compliance-filings-reads';
import { DOCUMENT_ROLE_NAMES } from './compliance-panel-model';
import { listAllArtifacts } from './compliance-queries';
import { COMPLIANCE_PAGE_TEST_IDS } from './compliance-routes';
import { ModuleUnavailableNotice } from './compliance-shared';
import { UploadDocumentsSheet } from './upload-sheet';

export { DOCUMENT_CUSTODY_COPY } from './upload-sheet';

const PREFIX = 'compliance-documents';

/** The subtitle, by the organization's AI setting (frames 06 and 12). */
const SUBTITLE_AI_ON = 'Kept as uploaded. Givmo doesn’t read documents yet; that comes later.';
const SUBTITLE_AI_OFF =
  'Kept as uploaded. AI is off for your organization, so Givmo never reads them.';

/**
 * What a refused removal says: a filing now names the document, so it stays (rulings log item 11,
 * 2026-10-01, replacing the retired register's name).
 */
export const DOCUMENT_STAYS_ON_FILE_COPY =
  'That document is now part of a filing, so it stays on file.';

/** The pages' header action that opens the upload sheet (frames 06, 07, 09, 12, 13). */
export function UploadDocumentsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      leadingIcon={<Icon icon={Upload} size={14} aria-hidden />}
      onClick={onClick}
      testId={`${PREFIX}-upload-open`}
    >
      Upload documents
    </Button>
  );
}

/** One document, as the table reads it. */
interface DocumentListRow {
  artifact: ComplianceArtifact;
  uses: readonly DocumentUse[];
  /** The host's name for who added it; absent where the host cannot say. */
  addedBy?: string;
}

/**
 * A document's menu: Open, and Remove where the record allows it, confirmed in place.
 *
 * Below `sm` the table stacks each document into a block (`stackAt="sm"`): the menu is parked at the
 * block's top-right corner (the name's lane stops short of it), and the confirmation takes its own
 * line at the block's foot so it never covers the name it is about.
 */
function DocumentActions({
  artifact,
  canRemove,
  onRemoved,
}: {
  artifact: ComplianceArtifact;
  canRemove: boolean;
  onRemoved: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const download = useArtifactDownload(artifact.artifactId);
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => operations.deleteArtifact(orgRef ?? '', artifact.artifactId),
    onSuccess: () => {
      setConfirming(false);
      toast.success('Document removed.');
      onRemoved();
    },
    onError: (error: unknown) => {
      setConfirming(false);
      if (isArtifactCited(error)) {
        // The list said deletable and the wire disagreed — a filing was recorded against this
        // document somewhere else while this page was open. The refusal is PERMANENT, so this is
        // stated as a settled fact and the list is re-read; offering a retry would be offering a
        // door that is now closed for good. Jonathan's words (2026-10-01, rulings log item 11).
        toast.error(DOCUMENT_STAYS_ON_FILE_COPY);
        onRemoved();
        return;
      }
      toast.error(serverMessage(error, 'We couldn’t remove that. Please try again.'));
    },
  });

  // ⚠ ABSENT for a cited document (and for a viewer), not disabled. See the header.
  const deletable = canRemove && isArtifactDeletable(artifact);

  if (confirming) {
    return (
      // The table: laid over the row's right end from the menu's own spot, so no column moves while
      // the question is open. Stacked: a line of its own at the block's foot.
      <div className="relative ml-auto size-32 max-sm:static max-sm:size-auto">
        <span className="absolute right-0 top-1/2 z-10 flex -translate-y-1/2 items-center gap-8 whitespace-nowrap bg-surface pl-12 max-sm:static max-sm:translate-y-0 max-sm:justify-end max-sm:pl-0 max-sm:pt-10">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            testId={`${PREFIX}-remove-confirm-${artifact.artifactId}`}
          >
            Remove it
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            disabled={remove.isPending}
            onClick={() => setConfirming(false)}
            testId={`${PREFIX}-remove-cancel-${artifact.artifactId}`}
          >
            Keep
          </Button>
        </span>
      </div>
    );
  }
  return (
    <div className="max-sm:absolute max-sm:right-8 max-sm:top-8">
      <Menu.Root modal={false}>
        <Menu.Trigger
          saas
          render={
            <IconButton size="sm" tone="ghost" aria-label={`More for ${artifact.fileName}`} />
          }
          testId={`${PREFIX}-menu-${artifact.artifactId}`}
        >
          <Icon icon={MoreHorizontal} size={18} aria-hidden />
        </Menu.Trigger>
        <Menu.Content saas className="min-w-[160px]">
          <Menu.Item
            saas
            onClick={() => download.mutate()}
            testId={`${PREFIX}-open-${artifact.artifactId}`}
          >
            Open
          </Menu.Item>
          {deletable && (
            <Menu.Item
              saas
              onClick={() => setConfirming(true)}
              testId={`${PREFIX}-remove-${artifact.artifactId}`}
            >
              Remove
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu.Root>
    </div>
  );
}

/**
 * The table's columns (frames 06 and 12): the document and its size, when and by whom it was
 * added, what the record uses it for, and its menu.
 *
 * Below `sm` each row stacks (`stackAt="sm"`, the design system's cure for a table squeezed onto a
 * phone): the name heads the block, "Added" and "Added by" share a line, each word the column's own
 * header, and "Used for" takes its own line. A fact the page does not have (a dash on the desktop
 * table) is left out of the block rather than labelled with nothing. The `max-sm:` classes inside
 * the cells follow that breakpoint.
 */
function documentColumns(
  canRemove: boolean,
  onRemoved: () => void,
): DataTableColumn<DocumentListRow>[] {
  return [
    {
      id: 'document',
      header: 'Document',
      // Stacked: the name's lane stops short of the menu parked at the block's corner.
      stackedClassName: 'max-sm:pr-36',
      cell: ({ artifact }) => (
        <div className="flex min-w-0 flex-col gap-2 py-14 max-sm:py-0">
          <span className="max-w-[400px] truncate text-text-strong max-sm:max-w-none">
            {artifact.fileName}
          </span>
          {artifact.byteSize !== undefined && (
            <span className="text-s12 text-text-tertiary">
              {displayArtifactSize(artifact.byteSize)}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'added',
      header: 'Added',
      stackedLabel: 'Added',
      cell: ({ artifact }) => (
        <span className="whitespace-nowrap tabular-nums">
          {displayLocalDate(artifact.uploadedAt)}
        </span>
      ),
    },
    {
      id: 'added-by',
      header: 'Added by',
      cell: ({ artifact, addedBy }) => (
        <span {...testIdProp(`${PREFIX}-added-by-${artifact.artifactId}`)}>
          {addedBy !== undefined ? (
            <>
              <span className="hidden max-sm:inline">Added by </span>
              {addedBy}
            </>
          ) : (
            <span className="max-sm:hidden">—</span>
          )}
        </span>
      ),
    },
    {
      id: 'used-for',
      header: 'Used for',
      stacked: 'line',
      cell: ({ artifact, uses }) => (
        <div
          className="flex flex-col gap-6 py-14 max-sm:py-0"
          {...testIdProp(`${PREFIX}-used-for-${artifact.artifactId}`)}
        >
          {uses.length === 0 ? (
            <span className="text-text-tertiary max-sm:hidden">—</span>
          ) : (
            uses.map((use, index) => (
              <div key={`${use.filingKey}:${use.role}`} className="flex flex-col gap-2">
                <span>
                  {index === 0 && <span className="hidden max-sm:inline">Used for </span>}
                  {use.title}
                </span>
                <span className="text-s12 text-text-tertiary">{DOCUMENT_ROLE_NAMES[use.role]}</span>
              </div>
            ))
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      width: '56px',
      stacked: 'line',
      cell: ({ artifact }) => (
        <DocumentActions artifact={artifact} canRemove={canRemove} onRemoved={onRemoved} />
      ),
    },
  ];
}

export function ComplianceDocumentsPage() {
  const queryClient = useQueryClient();
  const {
    orgRef,
    isReady,
    isPending: orgPending,
    isError: orgError,
    error,
    operations,
    moduleStanding,
    canWrite,
    personName,
  } = useCompliance();
  // The host's D-CPO-24 answer. Nothing below schedules a read or renders the data region without
  // an affirmative `'available'`; the host's gate renders every other state (compliance-standing.ts).
  const available = moduleStanding === 'available';
  const enabled = isReady && available && orgRef !== null;
  const [uploading, setUploading] = useState(false);

  // ⚠ EXHAUSTIVE. A document stranded on page 2 is a receipt the organization holds, cannot find,
  // and is told to upload again.
  const artifacts = useQuery({
    queryKey: ['compliance', 'artifacts', orgRef],
    queryFn: () => listAllArtifacts(operations, orgRef ?? ''),
    enabled,
  });
  const aiSetting = useQuery({
    queryKey: complianceAiSettingQueryKey(orgRef),
    queryFn: () => operations.getAiSetting(orgRef ?? ''),
    enabled,
  });
  // "Used for": what the organization's record points each document at.
  const composition = useFilingsComposition(operations, enabled ? orgRef : null);
  const libraryIds = (composition.rows ?? [])
    .filter((row) => row.ref.kind === 'library')
    .map((row) => row.ref.id);
  const obligationDocuments = useQueries({
    queries: libraryIds.map((obligationId) => ({
      queryKey: ['compliance', 'obligation-artifacts', orgRef, obligationId],
      queryFn: () => operations.listObligationArtifacts(orgRef ?? '', obligationId),
      enabled,
    })),
  });

  const moduleDark = artifacts.error != null && isModuleUnavailable(artifacts.error);
  // The bucket being unconfigured is NOT the module being dark, and the two must not collapse: the
  // filings still work, so the page says only that documents cannot be recorded here yet.
  const storageDark = artifacts.error != null && isVaultStorageUnavailable(artifacts.error);
  // Newest first (frame 12).
  const rows = extractArtifacts(artifacts.data).sort((a, b) =>
    (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''),
  );
  const uses = documentUses({
    rows: composition.rows ?? [],
    cycles: composition.records?.cycles ?? [],
    obligationDocuments: new Map<string, readonly ObligationArtifact[]>(
      libraryIds.map((obligationId, index) => [
        obligationId,
        extractObligationArtifacts(obligationDocuments[index]?.data),
      ]),
    ),
    ownFilingCycles: new Map(
      [...(composition.records?.ownFilingDetails ?? new Map())].map(([filingId, detail]) => [
        filingId,
        detail.cycles,
      ]),
    ),
  });
  const setting = decodeComplianceAiSetting(aiSetting.data);
  const subtitle =
    setting === null ? undefined : setting.givmoAiEnabled ? SUBTITLE_AI_ON : SUBTITLE_AI_OFF;
  const canUpload = canWrite === true && !storageDark;

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['compliance'] });
  }

  return (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-28 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.documents)}
    >
      <PageHeader
        title="Documents"
        {...(subtitle !== undefined ? { subtitle } : {})}
        {...(canUpload
          ? { actions: <UploadDocumentsButton onClick={() => setUploading(true)} /> }
          : {})}
      />

      {orgError && (
        <EmptyState
          testId={`${PREFIX}-org-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={serverMessage(error, 'Please try again.')}
        />
      )}

      {!orgError && !isReady && orgPending && <Skeleton className="h-[280px] w-full" />}

      {isReady && available && moduleDark && (
        <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
      )}

      {isReady && available && !moduleDark && (
        <>
          {artifacts.isPending && (
            <Skeleton className="h-[280px] w-full" {...testIdProp(`${PREFIX}-loading`)} />
          )}

          {!artifacts.isPending && storageDark && (
            <p className="text-s13 text-neutral-700" {...testIdProp(`${PREFIX}-no-storage`)}>
              {VAULT_UNAVAILABLE_COPY}
            </p>
          )}

          {/* ⚠ NOT SHOWN WHEN STORAGE IS UNAVAILABLE. The list being empty under a 503 does not mean
              the organization has uploaded nothing — it means the read was refused. */}
          {!artifacts.isPending && !storageDark && rows.length === 0 && (
            <EmptyState
              testId={`${PREFIX}-empty`}
              icon={<Icon icon={FolderOpen} size={20} aria-hidden />}
              title="No documents yet"
              description="Confirmation letters and your own filing paperwork appear here once you upload them."
            />
          )}

          {!artifacts.isPending && rows.length > 0 && (
            <DataTable
              testId={`${PREFIX}-table`}
              columns={documentColumns(canWrite === true, invalidate)}
              rows={rows.map((artifact): DocumentListRow => {
                const addedBy =
                  artifact.uploadedByUserId !== undefined
                    ? personName?.(artifact.uploadedByUserId)
                    : undefined;
                return {
                  artifact,
                  uses: uses.get(artifact.artifactId) ?? [],
                  ...(addedBy !== undefined ? { addedBy } : {}),
                };
              })}
              rowKey={({ artifact }) => artifact.artifactId}
              rowTestId={({ artifact }) => `${PREFIX}-row-${artifact.artifactId}`}
              stackAt="sm"
            />
          )}
        </>
      )}

      {canUpload && <UploadDocumentsSheet open={uploading} onClose={() => setUploading(false)} />}
    </div>
  );
}
